// SocketContext.tsx
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { io, Socket } from "socket.io-client";
import { Context } from "..";
import { IRespNotification } from "../common/api_interfaces/notifications/IRespNotification";
import { IGetNotifications } from "../common/api_interfaces/notifications/IGetNotifications";

interface ISocketContext {
    notification: IRespNotification;
    changeNotification: (data: IRespNotification) => void;
    isConnected: boolean;
}

const SocketContext = createContext<ISocketContext | null>(null);

export const useSocketContext = () => {
    const context = useContext(SocketContext);
    if (!context) {
        throw new Error("useSocketContext должен использоваться внутри <SocketContextProvider>");
    }
    return context;
};

export const SocketContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [socket, setSocket] = useState<Socket | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    const [notification, setNotification] = useState<IRespNotification>({
        data: { notifications: [], allCount: 0, countNotViewedData: 0 },
    });

    const { store } = useContext(Context);
    const socketRef = useRef<Socket | null>(null);
    const isAuthRef = useRef(store.isAuth);
    const reconnectTimeoutRef = useRef<NodeJS.Timeout>();

    // Обновляем ref при изменении авторизации
    useEffect(() => {
        isAuthRef.current = store.isAuth;
    }, [store.isAuth]);

    const changeNotification = useCallback((data: IRespNotification) => {
        setNotification(data);
    }, []);

    const disconnectSocket = useCallback(() => {
        if (socketRef.current) {
            socketRef.current.off("notification");
            socketRef.current.off("connect");
            socketRef.current.off("connect_error");
            socketRef.current.off("disconnect");
            socketRef.current.disconnect();
            socketRef.current = null;
        }
        setSocket(null);
        setIsConnected(false);
        
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
        }
    }, []);

    const connectSocket = useCallback(() => {
        if (!isAuthRef.current || socketRef.current?.connected) {
            return;
        }

        disconnectSocket(); // Очищаем предыдущее подключение

        let urlSocket = store.getSocketUrl();
        if (window.location.hostname === "localhost") {
            urlSocket += ":3000";
        }

        const newSocket = io(urlSocket, {
            withCredentials: true,
            transports: ["websocket"],
            autoConnect: true,
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
        });

        newSocket.on("connect", () => {
            console.log("Socket connected");
            setIsConnected(true);
        });

        newSocket.on("notification", (data: IRespNotification) => {
            if (!data?.data) return;

            setNotification((prev) => {
                const prevNotifications = prev.data?.notifications || [];
                const newNotifications = data.data?.notifications || [];
                 
                // Фильтруем дубликаты по id
                const uniqueNewNotifications = newNotifications.filter(
                    newNotif => !prevNotifications.some(prevNotif => prevNotif.id === newNotif.id)
                );

                const updatedNotifications = [...uniqueNewNotifications, ...prevNotifications];
                
                return {
                    data: {
                        notifications: updatedNotifications.slice(0, 50), // Ограничиваем количество
                        allCount: (prev.data?.allCount || 0) + (data.data?.allCount || 0),
                        countNotViewedData: (prev.data?.countNotViewedData || 0) + (data.data?.countNotViewedData || 0),
                    },
                };
            });
        });

        newSocket.on("connect_error", (error) => {
            console.error("Ошибка подключения сокета:", error);
            setIsConnected(false);
            
            // Пытаемся переподключиться через 5 секунд
            reconnectTimeoutRef.current = setTimeout(() => {
                if (isAuthRef.current) {
                    connectSocket();
                }
            }, 5000);
        });

        newSocket.on("disconnect", (reason) => {
            console.log("Socket disconnected:", reason);
            setIsConnected(false);
            
            if (reason === "io server disconnect") {
                reconnectTimeoutRef.current = setTimeout(() => {
                    if (isAuthRef.current) {
                        connectSocket();
                    }
                }, 1000);
            }
        });

        socketRef.current = newSocket;
        setSocket(newSocket);
    }, [store.getSocketUrl, disconnectSocket]);

    const fetchInitialNotifications = useCallback(async () => {
        if (!store.isAuth) return;

        const reqBody: IGetNotifications = {
            skip: 0,
            take: 20,
            filters: {
                onlyNotViewed: false,
                mention: true,
            },
            sort: {
                date: "desc",
            },
        };

        try {
            const newNotification = await store.notificationService.getNotifications(reqBody);
            if (newNotification?.data?.data) {
                setNotification(newNotification.data);
            }
        } catch (err) {
            console.error("Ошибка получения уведомлений:", err);
        }
    }, [store.isAuth, store.notificationService]);

    useEffect(() => {
        if (store.isAuth) {
            fetchInitialNotifications();
            connectSocket();
        } else {
            disconnectSocket();
            setNotification({ data: { notifications: [], allCount: 0, countNotViewedData: 0 } });
        }

        return () => {
            disconnectSocket();
        };
    }, [store.isAuth, connectSocket, disconnectSocket, fetchInitialNotifications]);

    useEffect(() => {
        return () => {
            disconnectSocket();
        };
    }, [disconnectSocket]);

    const contextValue = React.useMemo(() => ({
        notification,
        changeNotification,
        isConnected
    }), [notification, changeNotification, isConnected]);

    return (
        <SocketContext.Provider value={contextValue}>
            {children}
        </SocketContext.Provider>
    );
};